import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  BellRinging, BellSimple, Trash, PencilSimple, Check, X, Sparkle, Warning,
} from "@phosphor-icons/react";

/**
 * PestAlertPanel — inline widget on /farmers/pest-heatmap.
 *
 * Sign-in required. Lets a farmer subscribe to threshold-based pest
 * alerts around the current heatmap centre. Alerts fire when the current
 * window count rises by threshold_pct vs. the previous same-length window
 * — as long as the current count meets min_reports and the cooldown has
 * elapsed. Notifications arrive in the app's Alerts bell.
 */
export default function PestAlertPanel({ latitude, longitude, radiusKm, days, category }) {
  const { user, requireSignIn } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "Nearby pest & disease",
    threshold_pct: 50,
    min_reports: 3,
    cooldown_hours: 24,
    days_window: days || 7,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    http.get("/farmers/alerts")
      .then(({ data }) => setAlerts(data.alerts || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);
  useEffect(() => { setForm((f) => ({ ...f, days_window: days || 7 })); }, [days]);

  const canSubscribe = latitude !== null && longitude !== null;

  const create = async () => {
    if (!user) return requireSignIn("subscribe to pest alerts");
    if (!canSubscribe) return toast.error("Set your location on the map first.");
    setSaving(true);
    try {
      await http.post("/farmers/alerts", {
        name: form.name.trim() || "Nearby pest & disease",
        latitude, longitude,
        radius_km: radiusKm,
        days_window: parseInt(form.days_window, 10) || 7,
        threshold_pct: parseInt(form.threshold_pct, 10) || 50,
        min_reports: parseInt(form.min_reports, 10) || 3,
        cooldown_hours: parseInt(form.cooldown_hours, 10) || 24,
        categories: category ? [category] : ["pest", "disease", "insect"],
      });
      toast.success("Alert subscription created");
      setShowForm(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setSaving(false); }
  };

  const remove = async (aid) => {
    if (!window.confirm("Delete this alert?")) return;
    try {
      await http.delete(`/farmers/alerts/${aid}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const toggle = async (a) => {
    try {
      await http.patch(`/farmers/alerts/${a.id}`, { enabled: !a.enabled });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const testAlert = async (aid) => {
    setTesting(true);
    try {
      const { data } = await http.post(`/farmers/alerts/test/${aid}`);
      toast[data.would_fire ? "success" : "info"](
        data.would_fire
          ? `Would fire · current ${data.current} vs previous ${data.previous} (+${data.delta_pct}%)`
          : `Wouldn't fire yet · current ${data.current}, previous ${data.previous} (Δ${data.delta_pct}%)`,
      );
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not test"); }
    finally { setTesting(false); }
  };

  return (
    <section className="rounded-2xl border border-black/10 bg-white p-5" data-testid="pest-alert-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
          <BellRinging size={12} weight="fill" /> Alert me if reports rise
        </div>
        {user && (
          <button
            onClick={() => setShowForm(!showForm)}
            disabled={!canSubscribe}
            data-testid="pest-alert-toggle-form"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-3.5 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90 disabled:opacity-40"
          >
            {showForm ? "Cancel" : (canSubscribe ? "New alert" : "Set location first")}
          </button>
        )}
      </div>

      {!user && (
        <div className="mt-3 rounded-lg border border-dashed border-black/20 p-4 bg-[#FBF2E1]/40 text-sm text-[#3A3830] flex items-start gap-2" data-testid="pest-alert-signin">
          <BellSimple size={14} weight="fill" className="mt-0.5 text-[color:var(--np-brand,#B85F2A)]" />
          <div>
            <div>Sign in to be alerted when pest and disease pressure rises within your radius.</div>
            <button onClick={() => requireSignIn("subscribe to pest alerts")} data-testid="pest-alert-signin-cta" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90">
              Sign in
            </button>
          </div>
        </div>
      )}

      {user && showForm && (
        <div className="mt-3 rounded-lg border border-black/10 bg-[#FBF2E1]/40 p-4 space-y-3" data-testid="pest-alert-form">
          <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
            Centered on {latitude?.toFixed(3)}, {longitude?.toFixed(3)} · {radiusKm} km {category ? `· category: ${category}` : ""}
          </div>
          <label className="block text-xs">
            <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pest-alert-name" className="mt-1 w-full bg-white border border-black/15 rounded px-2 py-1.5 text-sm" />
          </label>
          <div className="grid grid-cols-3 gap-2">
            <label className="block text-xs">
              <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Rise %</span>
              <input type="number" min="10" max="500" value={form.threshold_pct} onChange={(e) => setForm({ ...form, threshold_pct: e.target.value })} data-testid="pest-alert-threshold" className="mt-1 w-full bg-white border border-black/15 rounded px-2 py-1.5 text-sm" />
            </label>
            <label className="block text-xs">
              <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Min reports</span>
              <input type="number" min="1" max="100" value={form.min_reports} onChange={(e) => setForm({ ...form, min_reports: e.target.value })} data-testid="pest-alert-min" className="mt-1 w-full bg-white border border-black/15 rounded px-2 py-1.5 text-sm" />
            </label>
            <label className="block text-xs">
              <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Cooldown h</span>
              <input type="number" min="1" max="168" value={form.cooldown_hours} onChange={(e) => setForm({ ...form, cooldown_hours: e.target.value })} data-testid="pest-alert-cooldown" className="mt-1 w-full bg-white border border-black/15 rounded px-2 py-1.5 text-sm" />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">Cancel</button>
            <button onClick={create} disabled={saving} data-testid="pest-alert-submit" className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
              <Check size={11} weight="bold" /> {saving ? "Saving…" : "Create alert"}
            </button>
          </div>
        </div>
      )}

      {user && (
        <div className="mt-3 space-y-2" data-testid="pest-alert-list">
          {loading && <div className="text-sm text-[#3A3830]">Loading…</div>}
          {!loading && alerts.length === 0 && (
            <div className="text-sm text-[#3A3830]">No alerts yet. Create one above to be pinged when pressure rises.</div>
          )}
          {alerts.map((a) => (
            <div key={a.id} data-testid={`pest-alert-${a.id}`} className="rounded-lg border border-black/10 bg-white p-3 flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
                  <BellRinging size={11} weight="fill" /> {a.enabled ? "Enabled" : "Paused"}
                  <span className="text-[#3A3830]">·</span>
                  <span>{a.radius_km} km · {a.days_window}d · +{a.threshold_pct}%</span>
                </div>
                <div className="mt-0.5 font-serif-display text-base">{a.name}</div>
                <div className="mt-0.5 text-[10px] text-[#3A3830]">
                  {a.categories?.length > 0 ? a.categories.join(" · ") : "pest · disease · insect"}
                  {typeof a.fire_count === "number" && ` · fired ${a.fire_count}×`}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => testAlert(a.id)} disabled={testing} data-testid={`pest-alert-test-${a.id}`} className="rounded-full border border-black/15 bg-white px-2 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5 disabled:opacity-50">
                  <Sparkle size={10} weight="fill" className="inline mr-1" /> Test
                </button>
                <button onClick={() => toggle(a)} data-testid={`pest-alert-toggle-${a.id}`} className="rounded-full border border-black/15 bg-white px-2 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5">
                  {a.enabled ? "Pause" : "Resume"}
                </button>
                <button onClick={() => remove(a.id)} data-testid={`pest-alert-delete-${a.id}`} className="rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 p-1">
                  <Trash size={11} weight="fill" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
